import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Handshake, Scale, MessageSquare, ShieldCheck, Clock, Check, Trophy, Building2, HeartCrack } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FeedbackSection } from "@/components/FeedbackSection";

const IMG = "https://images.pexels.com/photos/7643738/pexels-photo-7643738.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1400";

const steps = [
  { n: "01", t: "Understanding", d: "Confidential intake of each party's position, the commercial reality and the financial issues at the heart of the dispute." },
  { n: "02", t: "Facilitation", d: "Impartial sessions that facilitate and support the parties in generating options, helping them understand the financial issues between them." },
  { n: "03", t: "Resolution", d: "A balanced agreement reached by the parties themselves — durable because it is theirs, with the financial issues fully understood." },
];

export default function Mediation() {
  return (
    <div className="pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16,1,0.3,1] }}>
            <div className="overline mb-6">Service 02 · Commercial &amp; Civil Mediation</div>
            <h1 className="font-display font-light text-5xl lg:text-6xl leading-[1.05]">Impartial mediation, informed by financial expertise.</h1>
            <p className="text-zinc-400 text-lg mt-8 leading-relaxed">
              A balanced, neutral approach — with the distinct advantage of a mediator who genuinely understands business and finance. The role is to facilitate and support the parties in generating options to reach a balanced agreement.
            </p>
            <Link to="/contact" data-testid="mediation-enquiry-btn" className="btn-metal inline-flex px-7 py-3.5 rounded-full text-sm font-medium mt-9">Arrange a mediation</Link>
          </motion.div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl overflow-hidden border border-white/10 h-[52vh] max-h-[520px]">
              <img src={IMG} alt="Mediation and resolution" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <Reveal><div className="overline mb-4">The Tactis process</div>
        <h2 className="font-display font-light text-4xl lg:text-5xl max-w-2xl leading-tight">From entrenched positions to durable agreement.</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-9 h-full">
                <div className="font-display text-5xl text-silver">{s.n}</div>
                <h3 className="font-dash text-xl mt-6">{s.t}</h3>
                <p className="text-zinc-500 mt-3 leading-relaxed text-sm">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Distinct areas of mediation */}
      <section className="border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <Reveal>
            <div className="overline mb-4">Areas of Mediation</div>
            <h2 className="font-display font-light text-4xl lg:text-5xl max-w-3xl leading-tight">Distinct areas, one balanced approach.</h2>
            <p className="text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              Whatever the nature of the dispute, the method is the same — impartial facilitation that supports the parties in discussing options and reaching a balanced agreement.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {[
              { icon: HeartCrack, t: "Marital Asset Division", d: "Balanced facilitation for separating couples on the division of businesses, property, pensions and complex or cross-border assets." },
              { icon: Trophy, t: "Sports Contractual Mediation", d: "Contractual disputes between elite athletes, agents and clubs — image rights, bonuses, transfers, commission and terms." },
              { icon: Building2, t: "Commercial Mediation for Businesses", d: "Shareholder disputes, SPA (share purchase agreement) disputes, commercial property dilapidations, partnership and boardroom disagreements resolved commercially." },
              { icon: Handshake, t: "Other Civil & Commercial Disputes", d: "Any matter that benefits from balanced facilitation to discuss options and reach a durable, agreed outcome." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={(i % 4) * 0.06}>
                <div className="p-7 rounded-xl border border-white/10 bg-[#0d0d0d] h-full">
                  <c.icon size={26} strokeWidth={1} className="text-zinc-300" />
                  <h3 className="font-dash text-lg mt-6">{c.t}</h3>
                  <p className="text-zinc-500 text-sm mt-3 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#080808] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
          {[
            { icon: ShieldCheck, t: "Impartial", d: "Genuinely neutral to both sides." },
            { icon: Scale, t: "Financially literate", d: "Numbers understood, not just heard." },
            { icon: MessageSquare, t: "Confidential", d: "Without-prejudice throughout." },
            { icon: Clock, t: "Efficient", d: "Faster and lower-cost than trial." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.06}>
              <div className="p-6 rounded-xl border border-white/10 bg-[#0b0b0b] h-full">
                <c.icon size={24} strokeWidth={1} className="text-zinc-400" />
                <h3 className="font-dash text-lg mt-5">{c.t}</h3>
                <p className="text-zinc-500 text-sm mt-2">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FeedbackSection />
    </div>
  );
}
